import { useEffect, useRef, useState } from 'react'
import { API_BASE } from '@/lib/api'
import type { MediaRef } from '@/lib/content.types'

/**
 * 미디어 경로를 실제로 불러올 수 있는 URL로 바꾼다.
 * - /uploads/... (Admin 업로드) → API 서버를 가리켜야 하므로 API_BASE를 붙인다.
 * - /assets/...  (정적 자산)     → 웹 번들에 포함되므로 그대로 사용한다.
 * - http(s)://   (외부 URL/CDN)  → 그대로 사용한다.
 */
export function resolveMediaUrl(src: string): string {
  if (!src) return ''
  if (/^(https?:)?\/\//.test(src) || src.startsWith('data:')) return src
  if (src.startsWith('/uploads/')) return `${API_BASE}${src}`
  return src
}

interface MediaProps {
  media: MediaRef
  /**
   * 지정하면 기본 클래스를 완전히 대체한다.
   * (로고처럼 `h-[26px] w-auto object-contain` 이 필요한 경우 사용)
   */
  className?: string
  /** 첫 화면에 보이는 이미지는 eager, 나머지는 lazy */
  loading?: 'eager' | 'lazy'
  /**
   * true(기본)면 소리 없이 반복 재생되는 배경 영상으로 다룬다.
   * false면 재생 컨트롤을 노출하고 자동 재생하지 않는다.
   */
  ambient?: boolean
  /**
   * 겹쳐 놓은 미디어 중 지금 보여지는 것인지.
   * (Hub 산업 탭, VFX 클립처럼 여러 장을 포개 놓는 곳에서 사용)
   * false면 재생하지 않고 처음으로 되감으며, 파일도 미리 받지 않는다.
   */
  active?: boolean
  /**
   * 반복 재생 여부 (기본 true).
   * false로 두어야 영상이 끝날 때 `onEnded` 가 호출된다.
   */
  loop?: boolean
  /** 영상이 끝났을 때 (loop=false 일 때만 발생) */
  onEnded?: () => void
  /**
   * 실제 재생 상태가 바뀔 때 알려준다.
   * VFX 캐러셀이 "영상이 진짜 재생 중인지"를 알아야 타이머를 끌 수 있어서 필요하다.
   * (자동 재생이 막히거나 로드에 실패하면 false로 온다 → 캐러셀이 타이머로 폴백)
   */
  onPlayingChange?: (playing: boolean) => void
}

/** 이미지/영상을 종류에 맞게 렌더한다. 컨테이너를 꽉 채우도록 object-cover. */
export function Media(props: MediaProps) {
  if (props.media.kind === 'video') return <VideoMedia {...props} />

  const { media, className, loading = 'lazy' } = props
  return <img className={cls(className)} src={resolveMediaUrl(media.src)} alt={media.alt} loading={loading} decoding="async" />
}

function cls(className?: string): string {
  // 기본은 컨테이너를 꽉 채우는 object-cover. className을 주면 그 값으로 대체된다.
  return className ? `block ${className}` : 'block h-full w-full object-cover'
}

/** 재생을 시작할 노출 비율 */
const PLAY_RATIO = 0.25

/**
 * 배경 영상.
 *
 * **화면에 들어올 때 재생을 시작하고, 완전히 벗어나면 멈춘다.**
 * - 25% 이상 보이면 재생 시작
 * - **완전히 화면 밖으로 나가야** 일시정지 — 화면에 조금이라도 걸쳐 있으면 계속 재생된다
 *   (시작 기준과 정지 기준을 다르게 둔 것은 의도적이다. 같은 값을 쓰면 경계에서
 *    스크롤을 조금만 움직여도 재생/정지가 반복되며 깜빡인다)
 * - 다시 돌아오면 **멈췄던 지점부터 이어서** 재생한다
 * - 다른 탭으로 전환하면 일시정지, 돌아오면 재개
 * - 겹쳐 놓은 미디어 중 비활성(`active=false`)이면 재생하지 않고 처음으로 되감는다
 * - OS '동작 줄이기' 설정을 켠 사용자에게는 자동 재생하지 않는다 (포스터 이미지가 보인다)
 */
function VideoMedia({
  media,
  className,
  loading = 'lazy',
  ambient = true,
  active = true,
  loop = true,
  onEnded,
  onPlayingChange,
}: MediaProps) {
  const ref = useRef<HTMLVideoElement>(null)
  const [inView, setInView] = useState(false)
  const [tabVisible, setTabVisible] = useState(
    typeof document === 'undefined' ? true : !document.hidden,
  )

  // 화면에 들어왔는지 관찰
  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true) // 지원하지 않는 환경에서는 그냥 재생
      return
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.intersectionRatio
        // 영상이 화면보다 클 수 있다(세로로 긴 영상). 그때는 비율이 낮아도
        // 화면 절반 이상을 덮고 있으면 보이는 것으로 친다.
        const coversViewport = entry.intersectionRect.height >= window.innerHeight * 0.5

        if (ratio >= PLAY_RATIO || coversViewport) {
          setInView(true) // 충분히 보임 → 재생
        } else if (ratio === 0) {
          setInView(false) // 완전히 벗어남 → 정지
        }
        // 그 사이(0 < ratio < 0.25)는 현재 상태를 유지한다 = 깜빡임 방지
      },
      // 0(완전히 벗어남)과 0.25(재생 시작) 두 지점을 모두 감지해야 한다
      { threshold: [0, PLAY_RATIO] },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // 탭 전환 감지
  useEffect(() => {
    const onChange = () => setTabVisible(!document.hidden)
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])

  // 실제 재생/정지 결정
  useEffect(() => {
    const el = ref.current
    if (!el || !ambient) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const shouldPlay = inView && active && tabVisible && !reduced

    if (shouldPlay) {
      // 브라우저가 막으면 조용히 넘어간다 (예: 저전력 모드)
      void el.play().catch(() => undefined)
    } else {
      el.pause()
      // 캐러셀에서 벗어난 클립은 다음에 다시 볼 때 처음부터 보이도록 되감는다
      if (!active) el.currentTime = 0
    }
  }, [inView, active, tabVisible, ambient, media.src])

  return (
    <video
      ref={ref}
      className={cls(className)}
      src={resolveMediaUrl(media.src)}
      poster={media.poster ? resolveMediaUrl(media.poster) : undefined}
      aria-label={media.alt}
      muted={ambient}
      loop={ambient && loop}
      playsInline
      controls={!ambient}
      onEnded={onEnded}
      onPlaying={() => onPlayingChange?.(true)}
      onPause={() => onPlayingChange?.(false)}
      onError={() => onPlayingChange?.(false)}
      // 비활성 클립은 아예 받아오지 않는다 (VFX 5개가 동시에 다운로드되는 것 방지)
      preload={!active ? 'none' : loading === 'eager' ? 'auto' : 'metadata'}
    />
  )
}
